/**
 * Adapters between the Strapi blog API shape (`BlogPost` from
 * `@/types/blog`) and the UI shape that `BlogCard`, `BlogListClient`, and
 * the detail page already consume (`BlogPost` from `app/[locale]/blog/data/blogs.ts`).
 *
 * Kept in their own module so both the landing and detail pages can share
 * the same transformations without coupling components to the CMS shape.
 */

import { stripHtml } from '@/lib/homepage-mappers';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { BlogPost as CmsBlogPost } from '@/types/blog';
import type {
  BlogPost as UiBlogPost,
  BlogSection,
  BlogFaqItem as UiBlogFaq,
} from '@/app/[locale]/blog/data/blogs';

type MaybeString = string | null | undefined;

const PLACEHOLDER_IMAGE = MEDIA_PLACEHOLDER;

/** Average reading speed used by most blog platforms (~225 wpm). */
const WORDS_PER_MINUTE = 225;

/**
 * Strip HTML tags and decode common entities, then count whitespace-
 * separated tokens. Result is rounded up to the nearest minute and
 * capped at 1 min minimum so we never print "0 min read".
 */
export function computeReadTime(html: MaybeString): string {
  const text = stripHtml(html ?? '');
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const minutes = Math.max(1, Math.ceil(wordCount / WORDS_PER_MINUTE));
  return `${minutes} min read`;
}

/**
 * Format the publish date for the card + detail display. ISO date in,
 * "May 23, 2026" out. Falls back to the raw string if parsing fails so
 * we never blow up on an unexpected format.
 */
export function formatPublishDate(date: MaybeString): string {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Parse `<h2>...</h2>` tags out of the Strapi `blogContent` HTML to drive
 * the on-page Table of Contents. Each h2 becomes one section with a
 * slugified id (used both as the TOC link target and as `<section id>`).
 *
 * Paragraphs are NOT split per section — the detail page renders the raw
 * HTML via `dangerouslySetInnerHTML` in one block. The TOC just links to
 * the h2 anchors that the render pass injects.
 */
export function parseSectionsFromContent(html: MaybeString): BlogSection[] {
  if (!html) return [];
  const matches = Array.from(html.matchAll(/<h2[^>]*>([\s\S]*?)<\/h2>/gi));
  return matches.map((match, idx) => {
    const title = stripHtml(match[1]).trim();
    return {
      id: slugifyForAnchor(title) || `section-${idx + 1}`,
      title,
      paragraphs: [],
    };
  });
}

/**
 * Inject `id="..."` into every `<h2>` in the HTML so the TOC anchors
 * land on the correct heading when clicked. Idempotent — h2s that
 * already have an id are left alone.
 */
export function injectSectionAnchors(html: MaybeString): string {
  if (!html) return '';
  return html.replace(/<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi, (match, attrs, inner) => {
    if (attrs && /\bid=/.test(attrs)) return match;
    const id = slugifyForAnchor(stripHtml(inner).trim());
    if (!id) return match;
    return `<h2${attrs ?? ''} id="${id}">${inner}</h2>`;
  });
}

function slugifyForAnchor(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
}

/**
 * Map a CMS blog entry to the UI shape used by `BlogCard`,
 * `BlogListClient`, and the detail page. Keeps the existing components
 * untouched so the design is preserved automatically.
 *
 * `category` is empty because the CMS doesn't have one — `BlogListClient`
 * will still match on title + excerpt, which covers every realistic
 * search a user types.
 */
export function mapCmsBlogToUi(cms: CmsBlogPost): UiBlogPost {
  const firstImage = cms.blogFeaturedImage?.[0];
  const image = getStrapiMediaUrl(firstImage as never, 'medium') ?? PLACEHOLDER_IMAGE;
  const imageAlt = firstImage?.alternativeText || cms.blogTitle;

  const faqs: UiBlogFaq[] =
    cms.faqwithForm?.faqs?.map((f) => ({
      question: f.faqTitle,
      answer: stripHtml(f.faqContent),
    })) ?? [];

  return {
    id: cms.id,
    slug: cms.blogSlug,
    title: cms.blogTitle,
    excerpt: stripHtml(cms.blogExcerpt),
    date: formatPublishDate(cms.publishDate),
    image,
    imageAlt,
    readTime: computeReadTime(cms.blogContent),
    category: '',
    sections: parseSectionsFromContent(cms.blogContent),
    faqs,
  };
}
